import PrivateLayout from "@/components/private/PrivateLayout";
import { ADMIN_NAV } from "@/components/private/adminNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import {
  getZone,
  ZONE_COLORS as zoneColor,
  ZONE_LABELS as zoneLabel,
} from "@shared/scoring";
import { ArrowLeft, FileText, Plus } from "lucide-react";
import { Link, useRoute } from "wouter";

export default function AdminPlayerDetail() {
  const [, params] = useRoute<{ id: string }>("/admin/jugadores/:id");
  const id = Number(params?.id);
  const { data: player, isLoading } = trpc.players.getByIdAdmin.useQuery({ id }, { enabled: !!id });
  const { data: reports } = trpc.reports.listByPlayerAdmin.useQuery({ playerId: id }, { enabled: !!id });

  return (
    <PrivateLayout title="Administración" nav={ADMIN_NAV} requireRole="admin">
      <div className="p-6 md:p-12 max-w-6xl">
        <Link
          href="/admin/jugadores"
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--forest)] mb-6 hover:underline"
        >
          <ArrowLeft size={14} /> Volver a jugadores
        </Link>

        {isLoading ? (
          <div className="h-96 rounded-xl bg-white/50 animate-pulse" />
        ) : !player ? (
          <Card className="p-16 text-center">
            <p>Jugador no encontrado.</p>
          </Card>
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
              <div>
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--forest)]/70 mb-2">
                  Jugador
                </div>
                <h1 className="font-display font-light text-[var(--forest-deep)] leading-[1.05]" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}>
                  {player.fullName}
                </h1>
                <p className="mt-3 text-sm text-[var(--forest-deep)]/70">
                  {player.level || "Nivel no especificado"}
                  {player.handicap && ` · HCP ${player.handicap}`}
                  {player.email && ` · ${player.email}`}
                </p>
              </div>
              <Link href={`/admin/jugadores/${player.id}/informes/nuevo`}>
                <Button className="bg-[var(--forest)] hover:bg-[var(--forest-deep)] text-[var(--cream)] gap-2">
                  <Plus size={16} /> Nuevo informe
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
              <InfoBox label="Email" value={player.email || "—"} />
              <InfoBox label="Teléfono" value={player.phone || "—"} />
              <InfoBox label="Hándicap" value={player.handicap || "—"} />
              <InfoBox label="Años jugando" value={player.yearsPlaying?.toString() || "—"} />
              <InfoBox label="Altura" value={player.heightCm ? `${player.heightCm} cm` : "—"} />
              <InfoBox label="Peso" value={player.weightKg ? `${player.weightKg} kg` : "—"} />
              <InfoBox label="Dominancia" value={player.dominance || "—"} />
              <InfoBox label="Fecha alta" value={new Date(player.createdAt).toLocaleDateString("es-ES")} />
            </div>

            {player.objective && (
              <Card className="p-6 bg-white border border-[var(--forest-deep)]/10 mb-10">
                <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[var(--forest-deep)]/60 mb-2">
                  Objetivo
                </div>
                <p className="text-[var(--forest-deep)]/85 leading-relaxed">{player.objective}</p>
              </Card>
            )}

            <div>
              <h2 className="font-display text-2xl font-medium text-[var(--forest-deep)] mb-4">
                Informes
              </h2>
              {!reports || reports.length === 0 ? (
                <Card className="p-10 text-center bg-white border border-[var(--forest-deep)]/10">
                  <FileText className="mx-auto text-[var(--forest)]/30 mb-3" size={28} />
                  <p className="text-[var(--forest-deep)]/70">
                    Este jugador aún no tiene informes. Crea el primero con el botón superior.
                  </p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {reports.map((r: any) => {
                    const z = getZone(r.globalScore);
                    return (
                      <Link
                        key={r.id}
                        href={`/admin/informes/${r.id}`}
                        className="flex items-center gap-4 p-4 rounded-xl bg-white border border-[var(--forest-deep)]/10 hover:border-[var(--forest)]/40 transition-colors"
                      >
                        <div
                          className="h-10 w-10 rounded-full flex items-center justify-center font-mono-num text-sm font-semibold text-[var(--cream)]"
                          style={{ backgroundColor: zoneColor[z] }}
                        >
                          {r.globalScore}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--forest-deep)] truncate">{r.code}</div>
                          <div className="text-xs text-[var(--forest-deep)]/60 font-mono">
                            {new Date(r.assessmentDate).toLocaleDateString("es-ES")} · {zoneLabel[z]}
                          </div>
                        </div>
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded ${
                            r.status === "published"
                              ? "bg-[var(--forest)]/15 text-[var(--forest)]"
                              : "bg-[var(--amber-mustard)]/15 text-[var(--amber-mustard)]"
                          }`}
                        >
                          {r.status === "published" ? "Publicado" : "Borrador"}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
}

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-[var(--forest-deep)]/10">
      <div className="text-[10px] font-semibold tracking-wide uppercase text-[var(--forest-deep)]/60 mb-1">
        {label}
      </div>
      <div className="text-sm font-medium text-[var(--forest-deep)] truncate">{value}</div>
    </div>
  );
}
